// Contributo verde/blu di un singolo contabile a un totale AGGREGATO/
// CUMULATIVO (es. totale per cliente su tutto il periodo).
//
// Un Debito contribuisce per il suo importo pieno alla corsia del
// proprio `verde`. Un Pagamento Cliente contribuisce ZERO a entrambe
// (round 65-sexdecies — prima contribuiva per intero come un Debito: un
// bug, non una scelta, perché rompeva l'invariante che questa funzione
// promette da sempre — vedi contributoTotale sotto, che lo esclude da
// sempre): è un incasso, non fatturato, stessa esclusione già applicata
// a `contributoTotale`/`contributoFatturatoPeriodo` per lo stesso
// identico motivo (round 6/40). Le due funzioni sommate DEVONO trattare
// un Pagamento Cliente allo stesso modo, altrimenti "totale" smette di
// coincidere con "verde + blu" — esattamente il sintomo segnalato
// dall'utente (Pejrasso Tiziana: verde 10.446,52 + blu 41.283,16 =
// 51.729,68, ma "totale" mostrava 79.625,18 — la differenza, 27.895,50,
// coincideva esattamente con la somma dei suoi Pagamenti Cliente).
//
// Una Fattura Anticipata/Posticipata (tipo=3) è diversa: REGOLARIZZA
// ritiri verdi già esistenti (fuori tracciabilità DDT), non li affianca.
// Il suo imponibile si SPOSTA da verde a blu (sottratto da uno, aggiunto
// insieme all'IVA all'altro) — non si limita a sommarsi a blu. Esempio
// concreto confermato dall'utente: verde=1000€, blu=100€; si registra una
// fattura anticipata con imponibile 500€ + IVA 50€ → verde scende a 500€,
// blu sale a 650€ (100 + 500 + 50). Il totale complessivo si muove solo
// della sola IVA (50€): l'imponibile non è denaro nuovo, è la stessa
// somma già presente come debiti verdi, solo riclassificata.
//
// round 65-novodecies: si sposta solo la quota REALMENTE assegnata a un
// debito (`importo_imponibile - residuo - importo_estinto`), non
// l'intero imponibile dichiarato. Una fattura può avere una parte del
// proprio imponibile ancora libera (`residuo` erodibile, round
// 65-septendecies) — quella parte non ha mai regolarizzato nessun
// debito verde, non è "la stessa somma già presente come debiti verdi"
// promessa sopra, è credito inutilizzato a sé. Sottrarre l'imponibile
// intero comunque produceva un "Tot Verde" negativo e senza spiegazione
// per qualunque cliente con un credito erodibile ancora aperto (caso
// reale: Roagna Paolo, imponibile 2.535€ ma solo 2.301,15€ assegnati a
// debiti — verde risultava -93,85€ invece di +140€).
//
// Corretto SOLO su un totale che copre l'intero periodo/cliente: i debiti
// verdi regolarizzati possono essere di mesi/giorni diversi da quello in
// cui la fattura viene registrata — usare questa funzione su un bucket
// per singolo mese/giorno produrrebbe un "Tot Verde" negativo confuso in
// quel bucket, scollegato da qualunque movimento reale avvenuto lì (vedi
// contributoVerdeBluPeriodo per quel caso).
export function contributoVerdeBlu(contabile) {
  if (contabile.tipo === 2) {
    return { verde: 0, blu: 0 };
  }
  if (contabile.tipo === 3) {
    const imponibile = contabile.importo_imponibile || 0;
    const residuo = Math.max(0, contabile.residuo || 0);
    const estinto = contabile.importo_estinto || 0;
    const imponibileAssegnato = Math.max(0, imponibile - residuo - estinto);
    return {
      verde: -imponibileAssegnato,
      blu: imponibileAssegnato + (contabile.importo || 0),
    };
  }
  return {
    verde: contabile.verde ? contabile.importo : 0,
    blu: !contabile.verde ? contabile.importo : 0,
  };
}

// Contributo verde/blu di un singolo contabile a un totale per SINGOLO
// PERIODO (un giorno, un mese) — diverso dal cumulativo sopra apposta:
// qui una Fattura Anticipata/Posticipata contribuisce SOLO la propria IVA
// a blu, senza sottrarre nulla da verde. La riclassificazione (spostare
// l'imponibile da verde a blu) resta corretta solo guardando l'intero
// periodo/cliente nel suo complesso — nel dettaglio di un singolo
// mese/giorno mostrerebbe un "Tot Verde" negativo anche quando quel
// giorno/mese non ha avuto nessun movimento verde, perché i debiti che la
// fattura regolarizza sono quasi sempre di un periodo precedente diverso.
export function contributoVerdeBluPeriodo(contabile) {
  if (contabile.tipo === 3) {
    return {
      verde: 0,
      blu: contabile.importo || 0,
    };
  }
  return {
    verde: contabile.verde ? contabile.importo : 0,
    blu: !contabile.verde ? contabile.importo : 0,
  };
}

// Quanto fatturato (Debiti e Fatture Anticipate/Posticipate) in un
// singolo periodo, diviso per verde/blu — a differenza di
// contributoVerdeBluPeriodo, qui un Pagamento Cliente non contribuisce
// affatto: è un incasso, non un documento di fatturato, e mescolarlo
// diluirebbe la lettura di "quanto ho fatturato questo mese".
export function contributoFatturatoPeriodo(contabile) {
  if (contabile.tipo === 2) {
    return { verde: 0, blu: 0 };
  }
  return contributoVerdeBluPeriodo(contabile);
}

// "Totale" (mese o cumulativo per cliente): sempre indipendente dai
// Pagamenti Cliente — sono incassi, non fatturato, e non devono
// influenzare questo numero in nessuna modalità (né con "Solo Non
// Saldati" attivo né su "Tutti"). Un Debito contribuisce per intero, una
// Fattura Anticipata/Posticipata solo per la propria IVA (l'imponibile
// non compare mai qui: nel cumulativo la riclassificazione verde↔blu di
// contributoVerdeBlu si annulla comunque sommando verde+blu, quindi
// questo numero coincide sempre con "solo l'IVA", niente bisogno di due
// logiche diverse per mese e cumulativo). La gestione di "quanto ancora
// scoperto" è delegata al chip residuoAperto, non a questo numero.
export function contributoTotale(contabile) {
  return contabile.tipo === 2 ? 0 : contabile.importo || 0;
}

// Per un Debito (tipo=1) non saldato, scompone quanto ancora dovuto in due
// componenti che vanno SEMPRE tenute distinte (round 65-quater):
//
// - `scoperto`: la quota senza NESSUNA allocazione sopra (il `residuo`
//   annotato dal backend quando presente — vedi get_contabili). Resta
//   nella propria corsia verde/blu.
// - `inAttesa`: la quota già allocata a una Fattura di Copertura ma non
//   ancora realizzata in cassa (`in_attesa_fattura.importo`, già pesato
//   dal backend per la frazione di rate pagate — round 56-bis/57). Questa
//   quota è SEMPRE riclassificata blu (arriverà, se arriva, tramite il
//   canale della fattura — stesso principio di contributoVerdeBlu, round
//   28) e scende DA SOLA quando le rate vengono saldate, senza bisogno di
//   ricalcolarla qui.
//
// Se nessuno dei due segnali è presente (né `residuo`, né
// `importo_coperto`, né `in_attesa_fattura`) il debito non ha ricevuto
// nessuna allocazione: tutto l'importo resta scoperto, nella propria
// corsia. Prima di round 65-quater un debito interamente allocato a una
// fattura ferma allo 0% risultava con `residuo` assente per lo stesso
// motivo di un debito mai toccato — il chiamante non poteva distinguerli
// e contava l'intero importo come ancora scoperto IN VERDE, anche se già
// preso in carico da una fattura (doppio conteggio: una volta qui, una
// volta — correttamente — sull'IVA della fattura stessa).
function scopertoEInAttesaDebito(contabile) {
  if (
    contabile.residuo === undefined &&
    contabile.importo_coperto === undefined &&
    !contabile.in_attesa_fattura
  ) {
    return { scoperto: Math.abs(contabile.importo || 0), inAttesa: 0 };
  }
  return {
    scoperto: contabile.residuo || 0,
    inAttesa: contabile.in_attesa_fattura
      ? contabile.in_attesa_fattura.importo
      : 0,
  };
}

// Quanto resta davvero dovuto dal cliente su un singolo contabile.
//
// Round 65-octodecies: il chip di riepilogo per cliente (unico
// chiamante finora) non usa più questa funzione — sommava solo sui
// contabili del periodo/filtro selezionato, facendo sparire un
// debito/credito più vecchio della finestra scelta. Il chip ora prende
// gli stessi numeri, calcolati sull'intero storico senza vincolo di
// data, da un endpoint dedicato (get_aperture_riepilogo sul backend,
// store action fetch_aperture_riepilogo). Questa funzione e
// residuoApertoVerdeBlu restano corrette e documentate qui come
// riferimento per un eventuale futuro indicatore per-riga (nessun
// chiamante oggi, non è codice morto per un errore — solo per lo
// spostamento del calcolo lato server).
//
// Debito: somma delle due componenti sopra (scoperto + in attesa di
// fattura) — mai il solo importo pieno quando esiste già una qualunque
// allocazione. Zero se già saldato.
//
// Fattura Anticipata/Posticipata NON ancora saldata: solo l'IVA
// (`contabile.importo`) è realmente dovuta dal cliente come riga a sé —
// l'imponibile che finanzia (eroso o meno da un debito) non va sommato
// qui di nuovo, è già contato sopra come `inAttesa` sui debiti che
// finanzia (le due quote non si sovrappongono mai: insieme fanno esattamente
// imponibile+IVA non ancora incassati, vedi round 65-quater). Se la
// fattura è già saldata, zero anche per l'IVA — e se è pagata a rate
// (round 54+, caso raro) l'IVA ancora dovuta scende PROPORZIONALMENTE
// man mano che le rate vengono pagate (round 57-bis), stessa logica già
// applicata al debito che finanzia (get_contabili/ricalcola_saldato_debito):
// senza questo, un pagamento parziale abbatterebbe il residuo del debito
// ma lascerebbe l'IVA ancora intera nei totali aggregati, un conto che
// non torna (vedi discussione col cliente sull'esempio numerico).
//
// Pagamento Cliente: sempre saldato per definizione (round 6), non
// rappresenta mai denaro dovuto — zero sempre, come qualunque altro tipo
// diverso da Debito/Fattura Anticipata (stessa distinzione già fatta per
// il "Tot Giorno": Pagamenti/Fatture non sono "quanto il cliente deve").
export function residuoAperto(contabile) {
  if (contabile.tipo === 3) {
    if (contabile.saldato) return 0;
    const rate = contabile.rate_pagamento;
    if (rate && rate.totale > 0) {
      const frazionePagata = rate.incassato / rate.totale;
      return Math.max(0, (contabile.importo || 0) * (1 - frazionePagata));
    }
    return contabile.importo || 0;
  }
  if (contabile.tipo !== 1 || contabile.saldato) {
    return 0;
  }
  const { scoperto, inAttesa } = scopertoEInAttesaDebito(contabile);
  return scoperto + inAttesa;
}

// Suddivisione verde/blu di residuoAperto: la quota genuinamente scoperta
// di un Debito resta nella propria corsia (verde o blu); la quota già
// allocata a una Fattura di Copertura ma non ancora realizzata è SEMPRE
// blu (round 65-quater, stesso motivo di contributoVerdeBlu). Una Fattura
// Anticipata/Posticipata è invece sempre blu essa stessa (atto fiscale
// formale, mai un flag scelto) e la sua IVA ancora dovuta conta sempre
// come blu, anche se regolarizza debiti verdi a monte.
export function residuoApertoVerdeBlu(contabile) {
  if (contabile.tipo === 3) {
    const residuo = residuoAperto(contabile);
    return residuo > 0 ? { verde: 0, blu: residuo } : { verde: 0, blu: 0 };
  }
  if (contabile.tipo !== 1 || contabile.saldato) {
    return { verde: 0, blu: 0 };
  }
  const { scoperto, inAttesa } = scopertoEInAttesaDebito(contabile);
  return {
    verde: contabile.verde ? scoperto : 0,
    blu: (!contabile.verde ? scoperto : 0) + inAttesa,
  };
}
